import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type Ref,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Flex } from '../../flex';
import { mergeSx } from '../../utils';
import { TextField, type TextFieldProps } from '../TextField';

type OtpInputProps = Omit<
  TextFieldProps,
  | 'onChange'
  | 'value'
  | 'defaultValue'
  | 'onKeyDown'
  | 'onPaste'
  | 'inputRef'
  | 'inputProps'
  | 'slotProps'
  | 'label'
  | 'helperText'
  | 'id'
  | 'name'
  | 'onBlur'
> & {
  length?: number;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  ariaLabel?: string;
  id?: string;
  name?: string;
  label?: React.ReactNode;
  helperText?: React.ReactNode;
};

/**
 * Converts a fixed-length sparse string (spaces = empty slots) into a string[].
 * E.g. "1 3   " (length 6) → ['1', '', '3', '', '', '']
 */
function deserializeValue(value: string, length: number): string[] {
  const slots = Array.from({ length }, (_, i) => {
    const char = value[i];
    if (char === undefined || char === ' ') return '';
    if (/\d/.test(char)) return char;
    return '';
  });
  return slots;
}

/**
 * Converts a string[] into a fixed-length sparse string.
 * E.g. ['1', '', '3', '', '', ''] → "1 3   "
 */
function serializeValue(slots: string[]): string {
  return slots.map((s) => (s === '' ? ' ' : s)).join('');
}

function OtpInput(props: Readonly<OtpInputProps>, ref: Ref<HTMLDivElement>) {
  const {
    length = 6,
    value,
    defaultValue,
    onChange,
    onBlur,
    disabled,
    error,
    color = 'primary',
    sx,
    ariaLabel = 'One-time password input',
    id,
    name,
    label,
    helperText,
    ...restProps
  } = props;

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string[]>(() =>
    deserializeValue(defaultValue ?? '', length),
  );

  useEffect(() => {
    if (!isControlled) {
      setInternalValue(deserializeValue(defaultValue ?? '', length));
    }
  }, [defaultValue, isControlled, length]);

  // In controlled mode, derive slots from the external value string
  const otpValue = isControlled ? deserializeValue(value, length) : internalValue;

  const labelId = id ? `${id}-label` : undefined;
  const helperTextId = id ? `${id}-helper-text` : undefined;
  const groupAriaLabelledBy = label ? labelId : undefined;
  const groupAriaDescribedBy = helperText ? helperTextId : undefined;

  const setRefs = (element: HTMLDivElement | null) => {
    wrapperRef.current = element;

    if (typeof ref === 'function') {
      ref(element);
      return;
    }

    if (ref && 'current' in ref) {
      (ref as { current: HTMLDivElement | null }).current = element;
    }
  };

  const focusInput = (index: number, options?: { select?: boolean }) => {
    const input = inputRefs.current[index];

    if (!input) {
      return;
    }

    input.focus();

    if (options?.select) {
      input.select();
    }
  };

  const emitChange = (nextSlots: string[]) => {
    if (!isControlled) {
      setInternalValue(nextSlots);
    }

    onChange?.(serializeValue(nextSlots));
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(/\D/g, '');

    if (!inputValue) {
      const nextSlots = [...otpValue];
      nextSlots[index] = '';
      emitChange(nextSlots);
      return;
    }

    const nextSlots = [...otpValue];
    const nextDigits = inputValue.slice(0, length - index).split('');

    nextDigits.forEach((digit, digitIndex) => {
      nextSlots[index + digitIndex] = digit;
    });

    emitChange(nextSlots);

    const nextFocusIndex = Math.min(index + nextDigits.length, length - 1);

    if (nextFocusIndex !== index) {
      focusInput(nextFocusIndex, { select: nextFocusIndex !== length - 1 });
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (otpValue[index]) {
        // Clear only the current slot — do not touch any other slot
        const nextSlots = [...otpValue];
        nextSlots[index] = '';
        emitChange(nextSlots);
        return;
      }

      // Current slot is already empty — move focus back without deleting
      if (index > 0) {
        focusInput(index - 1, { select: true });
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1, { select: true });
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1, { select: true });
    }
  };

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedValue = event.clipboardData.getData('text').replace(/\D/g, '');

    if (!pastedValue) {
      return;
    }

    const nextSlots = [...otpValue];
    const nextDigits = pastedValue.slice(0, length - index).split('');

    nextDigits.forEach((digit, digitIndex) => {
      nextSlots[index + digitIndex] = digit;
    });

    emitChange(nextSlots);

    const nextFocusIndex = Math.min(index + nextDigits.length, length - 1);

    if (nextFocusIndex !== index) {
      focusInput(nextFocusIndex, { select: nextFocusIndex !== length - 1 });
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const nextFocusedElement = event.relatedTarget;

    if (nextFocusedElement instanceof Node && wrapperRef.current?.contains(nextFocusedElement)) {
      return;
    }

    onBlur?.(event as unknown as FocusEvent<HTMLDivElement>);
  };

  const mergedSx = mergeSx(
    {
      width: 'fit-content',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    sx,
  );

  return (
    <Flex ref={setRefs} sx={mergedSx} gap={1}>
      {label ? (
        <InputLabel id={labelId} sx={{ position: 'relative' }}>
          {label}
        </InputLabel>
      ) : null}
      <Flex
        gap={2}
        role='group'
        aria-label={groupAriaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={groupAriaLabelledBy}
        aria-describedby={groupAriaDescribedBy}
      >
        {otpValue.map((digit, index) => (
          <TextField
            {...restProps}
            key={index}
            id={id ? `${id}-${index}` : undefined}
            name={name ? `${name}-${index}` : undefined}
            value={digit}
            disabled={disabled}
            error={error}
            color={color}
            onBlur={handleBlur}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            inputRef={(element) => {
              inputRefs.current[index] = element;
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 1,
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              'aria-label': `Digit ${index + 1} of ${length}`,
              'aria-describedby': helperText ? helperTextId : undefined,
              style: {
                textAlign: 'center',
              },
            }}
            slotProps={{
              input: {
                sx: {
                  width: 52,
                  height: 52,
                  borderRadius: '8px',
                  borderColor: (theme) => {
                    if (error) {
                      return theme.palette.error.main;
                    }

                    return theme.palette.grey[300];
                  },
                  '&.Mui-focused': {
                    borderColor: (theme) => {
                      if (error) {
                        return theme.palette.error.main;
                      }

                      return theme.palette[color].main;
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    minHeight: '50px',
                    p: 0,
                    fontSize: 24,
                    fontWeight: 600,
                    lineHeight: 1,
                  },
                },
              },
            }}
            sx={{
              width: 52,
              '& .MuiFormHelperText-root': {
                display: 'none',
              },
              '& .MuiInputLabel-root': {
                display: 'none',
              },
            }}
          />
        ))}
      </Flex>
      {helperText ? <FormHelperText id={helperTextId}>{helperText}</FormHelperText> : null}
    </Flex>
  );
}

const ForwardedOtpInput = forwardRef(OtpInput);

ForwardedOtpInput.displayName = 'OtpInput';

export { ForwardedOtpInput as OtpInput };
export type { OtpInputProps };
